import { BrandKit } from "@redotech/redo-model/brand-kit";
import { MarketingForgeElementType } from "@redotech/redo-model/forge/marketing-signup-forge-types";
import { SignupForm as ISignupForm } from "@redotech/redo-model/signup-form-schema";
import { BrandKitButton } from "@redotech/redo-web/brand-kit-button";
import { Flex } from "@redotech/redo-web/flex";
import { SignupForm } from "@redotech/redo-web/marketing/signup-form";
import { MerchantCustomizableModal } from "@redotech/redo-web/storefront/merchant-customizable-modal";
import { RedoShopifyRpcClient } from "@redotech/shopify-sdk/rpc/client";
import { memo, useCallback, useState } from "react";

/**
 * Modal component for the Back in Stock form
 */
export const BackInStockFormModal = memo(function BackInStockFormModal({
  brandKit,
  client,
  form,
  formId,
  shopifyCustomerId,
  productId,
  variantId,
}: {
  brandKit: BrandKit;
  client?: RedoShopifyRpcClient;
  form: ISignupForm;
  formId: string;
  shopifyCustomerId: string | null;
  productId: string | null;
  variantId: string | null;
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [open, setOpen] = useState(false);

  const getNextStepIndex = useCallback(
    (startIndex: number): number => {
      if (!form) return startIndex;
      let stepIndex = startIndex;

      while (
        stepIndex < form?.steps.length &&
        form.steps[stepIndex]?.elements.some(
          (el) =>
            el.type === MarketingForgeElementType.INPUT &&
            el.inputType === "phone",
        )
      ) {
        stepIndex++;
      }

      return stepIndex;
    },
    [form],
  );

  const submitStep = useCallback(
    async (i: number, formData: Record<string, string>) => {
      if (
        !form ||
        !formId ||
        !client ||
        !productId ||
        !variantId ||
        !formData.email
      )
        return;

      const result = await client.submitBackInStockForm({
        email: formData.email,
        productId,
        variantId,
      });

      if (!result.success) {
        console.error("Failed to submit form step", result);
        return;
      }

      if (shopifyCustomerId) {
        await client.logSignupFormAnalytics({
          idempotencyKey: shopifyCustomerId,
          formId,
          type: "submit",
        });
      }

      const nextStepIndex = getNextStepIndex(i + 1);
      const hasNextStep = nextStepIndex < form.steps.length;

      if (hasNextStep) {
        setCurrentStep(nextStepIndex);
      } else {
        setCurrentStep(0);
        setOpen(false);
        setCompleted(true);
      }
    },
    [
      form,
      formId,
      client,
      getNextStepIndex,
      shopifyCustomerId,
      productId,
      variantId,
    ],
  );

  const openModal = useCallback(async () => {
    if (open) return;
    setOpen(true);

    const initialStep = getNextStepIndex(0);
    setCurrentStep(initialStep);

    if (client && formId && shopifyCustomerId) {
      await client.logSignupFormAnalytics({
        idempotencyKey: shopifyCustomerId,
        formId,
        type: "view",
      });
    }
  }, [open, getNextStepIndex, client, formId, shopifyCustomerId]);

  const opacityHex = form
    ? Math.floor((form.theme.overlay.opacityPercent / 100) * 255)
        .toString(16)
        .padStart(2, "0")
    : 0;

  return (
    <Flex justify="center" py="lg" w="full">
      <div style={{ width: "100%" }}>
        {form.pdpButton && (
          <BrandKitButton
            brandKit={brandKit}
            hierarchy={form.pdpButton.hierarchy}
            onClick={openModal}
            override={
              form.theme.overrides.buttons.enabled
                ? form.theme.overrides.buttons
                : undefined
            }
            pending={false}
            text={form.pdpButton.text}
            type="submit"
          />
        )}
      </div>
      {form && (
        <MerchantCustomizableModal
          backdropColor={form.theme.overlay.color + opacityHex}
          fullscreen={form.layout.type === "fullscreen"}
          onClose={() => setOpen(false)}
          open={open && !completed}
        >
          <SignupForm
            brandKit={brandKit}
            currentStep={currentStep}
            form={form}
            onClose={() => setOpen(false)}
            onSubmitStep={submitStep}
          />
        </MerchantCustomizableModal>
      )}
    </Flex>
  );
});
